import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Leaf, Tag, ShoppingBasket, MapPin, ChevronRight, MessageCircle,
    Sparkles, Clock, Mail, Phone, Navigation, Heart, ShieldCheck, Truck,
} from "lucide-react";
import PageShell from "@/components/PageShell";
import { useLang } from "@/context/LanguageContext";
import { useSettings, isOpeningSoon } from "@/context/SettingsContext";
import { api, resolveImage, formatApiError } from "@/lib/api";
import { pick } from "@/i18n/translations";
import { toast } from "sonner";

function buildWhatsappLink(phone, msg) {
    const cleaned = (phone || "").replace(/[^\d+]/g, "");
    const q = encodeURIComponent(msg || "");
    return `https://wa.me/${cleaned.replace(/^\+/, "")}${q ? `?text=${q}` : ""}`;
}

/* ---------- HERO ---------- */
function HeroSoon({ settings }) {
    const { t, lang } = useLang();
    const hero = pick(settings.hero_message, lang) || t.hero.soonTitleSuf;

    return (
        <section data-testid="hero-section" className="relative isolate overflow-hidden bg-brand-green text-white">
            <div className="absolute inset-0 grainy opacity-50" />
            <div className="absolute -top-32 -right-24 h-[520px] w-[520px] rounded-full bg-brand-green-soft/40 blur-3xl" />
            <div className="absolute -bottom-40 -left-20 h-[460px] w-[460px] rounded-full bg-brand-yellow/10 blur-3xl" />

            <div className="container-x relative grid lg:grid-cols-12 gap-10 py-20 md:py-28 lg:py-32 items-center">
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="eyebrow text-brand-yellow">
                        <span className="text-brand-yellow">{t.hero.eyebrow}</span>
                    </div>
                    <h1 className="mt-5 font-display font-extrabold leading-[0.95] tracking-tight text-balance">
                        <span className="block text-brand-red text-[64px] sm:text-[84px] lg:text-[108px]">{t.hero.soonTitlePre}</span>
                        <span className="block text-brand-yellow text-[28px] sm:text-[34px] lg:text-[40px] tracking-[0.45em] mt-1">{t.hero.soonTitleMid}</span>
                        <span className="block text-white text-3xl sm:text-4xl lg:text-5xl mt-6 font-bold">{hero}</span>
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-white/80 max-w-xl">{t.hero.soonSubtitle}</p>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <a
                            data-testid="hero-cta-whatsapp"
                            href={buildWhatsappLink(settings.whatsapp, "Hallo SCHOCK MARKT!")}
                            target="_blank" rel="noreferrer noopener"
                            className="btn-yellow"
                        >
                            <MessageCircle className="h-4 w-4" /> {t.hero.ctaWhats}
                        </a>
                        <a data-testid="hero-cta-location" href="#standort" className="btn-outline-light">
                            <MapPin className="h-4 w-4" /> {t.hero.ctaLoc}
                        </a>
                        <Link data-testid="hero-cta-offers" to="/angebote" className="btn-outline-light">
                            <Sparkles className="h-4 w-4" /> {t.hero.ctaOffers}
                        </Link>
                    </div>

                    <Countdown settings={settings} />
                </div>

                <div className="lg:col-span-5 relative animate-fade-in">
                    <HeroCollage />
                </div>
            </div>

            {/* footer ribbon */}
            <div className="border-t border-white/10 bg-black/20">
                <div className="container-x py-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs text-white/70">
                    <span className="inline-flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-brand-yellow" /> Qualität geprüft</span>
                    <span className="inline-flex items-center gap-2"><Heart className="h-3.5 w-3.5 text-brand-red" /> Regional &amp; Familie</span>
                    <span className="inline-flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-brand-yellow" /> Frisch geliefert</span>
                    <span className="inline-flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-brand-yellow" /> Mo–Sa Service</span>
                </div>
            </div>
        </section>
    );
}

function HeroLive({ settings }) {
    const { t } = useLang();
    return (
        <section data-testid="hero-section" className="relative overflow-hidden bg-brand-green text-white">
            <div className="absolute inset-0 grainy opacity-50" />
            <div className="container-x relative grid lg:grid-cols-12 gap-10 py-20 md:py-28 items-center">
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="eyebrow text-brand-yellow">{t.opening.jetztGeoeffnet}</div>
                    <h1 className="mt-5 font-display font-extrabold leading-[0.95] text-balance text-[56px] sm:text-[72px] lg:text-[88px]">
                        {t.hero.livedTitle}
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-white/80 max-w-xl">{t.hero.livedSubtitle}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link data-testid="hero-cta-offers" to="/angebote" className="btn-yellow">
                            <Sparkles className="h-4 w-4" /> {t.hero.ctaOffers}
                        </Link>
                        <a data-testid="hero-cta-location" href="#standort" className="btn-outline-light">
                            <MapPin className="h-4 w-4" /> {t.hero.ctaLoc}
                        </a>
                    </div>
                </div>
                <div className="lg:col-span-5"><HeroCollage /></div>
            </div>
        </section>
    );
}

function HeroCollage() {
    const imgs = [
        "https://images.pexels.com/photos/2101261/pexels-photo-2101261.jpeg",
        "https://images.pexels.com/photos/8112384/pexels-photo-8112384.jpeg",
        "https://images.pexels.com/photos/221442/pexels-photo-221442.jpeg",
        "https://images.unsplash.com/photo-1683314573422-649a3c6ad784",
    ];
    return (
        <div className="grid grid-cols-2 gap-3 md:gap-4">
            {imgs.map((src, i) => (
                <div
                    key={i}
                    className={`relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl ${i === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"}`}
                >
                    <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>
            ))}
        </div>
    );
}

function Countdown({ settings }) {
    const { t } = useLang();
    const enabled = settings.countdown_enabled && settings.opening_date;
    const [remaining, setRemaining] = useState({ d: 0, h: 0, m: 0, s: 0 });

    useEffect(() => {
        if (!enabled) return;
        const target = new Date(settings.opening_date).getTime();
        const tick = () => {
            const diff = Math.max(0, target - Date.now());
            const d = Math.floor(diff / 86400000);
            const h = Math.floor((diff % 86400000) / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setRemaining({ d, h, m, s });
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [enabled, settings.opening_date]);

    if (!enabled) {
        return (
            <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs text-white/70" data-testid="countdown-placeholder">
                <Clock className="h-3.5 w-3.5 text-brand-yellow" />
                {t.opening.placeholderDate}
            </div>
        );
    }
    const items = [
        { v: remaining.d, l: t.hero.countdownDays },
        { v: remaining.h, l: t.hero.countdownHours },
        { v: remaining.m, l: t.hero.countdownMin },
        { v: remaining.s, l: t.hero.countdownSec },
    ];
    return (
        <div className="mt-8 inline-grid grid-cols-4 gap-2 sm:gap-3" data-testid="countdown-live">
            {items.map((it, i) => (
                <div key={i} className="min-w-[68px] rounded-2xl border border-white/15 bg-white/5 px-3 py-3 text-center">
                    <div className="text-2xl sm:text-3xl font-display font-bold text-brand-yellow tabular-nums">{String(it.v).padStart(2, "0")}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-white/60">{it.l}</div>
                </div>
            ))}
        </div>
    );
}

/* ---------- VALUE PROPS ---------- */
function ValueProps() {
    const { t } = useLang();
    const icons = [Leaf, Tag, ShoppingBasket, MapPin];
    return (
        <section className="py-20 md:py-28">
            <div className="container-x">
                <div className="max-w-2xl">
                    <div className="eyebrow">{t.values.eyebrow}</div>
                    <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-balance">{t.values.title}</h2>
                    <p className="mt-4 text-base md:text-lg text-brand-charcoal/70">{t.values.subtitle}</p>
                </div>
                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {t.values.items.map((it, i) => {
                        const Icon = icons[i] || Leaf;
                        return (
                            <div key={i} className="group relative rounded-2xl border border-black/5 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_18px_50px_-25px_rgba(22,58,46,0.4)]" data-testid={`value-${i}`}>
                                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-green text-brand-yellow">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div className="mt-5 font-display text-xl font-semibold">{it.t}</div>
                                <p className="mt-2 text-sm text-brand-charcoal/70">{it.d}</p>
                                <div className="absolute right-5 top-5 text-[11px] font-mono text-brand-charcoal/30">0{i + 1}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ---------- CATEGORIES PREVIEW ---------- */
function CategoriesPreview({ categories }) {
    const { t, lang } = useLang();
    if (!categories?.length) return null;
    const top = categories.slice(0, 8);
    return (
        <section className="bg-brand-cream-dark/40 py-20 md:py-28">
            <div className="container-x">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div className="max-w-2xl">
                        <div className="eyebrow">{t.categories.eyebrow}</div>
                        <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-balance">{t.categories.title}</h2>
                        <p className="mt-4 text-base md:text-lg text-brand-charcoal/70">{t.categories.subtitle}</p>
                    </div>
                    <Link to="/kategorien" className="btn-outline-dark" data-testid="categories-all">
                        {t.cta.more} <ChevronRight className="h-4 w-4" />
                    </Link>
                </div>

                <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {top.map((c, i) => (
                        <Link
                            key={c.id || c.slug}
                            to="/kategorien"
                            data-testid={`category-card-${c.slug}`}
                            className={`group relative overflow-hidden rounded-2xl bg-brand-green text-white aspect-[4/5] ${i === 0 || i === 5 ? "md:row-span-2 md:aspect-auto" : ""}`}
                        >
                            <img src={resolveImage(c.image_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-110" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                            <div className="absolute inset-0 p-5 flex flex-col justify-end">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-yellow">0{i + 1}</div>
                                <div className="mt-2 font-display text-xl font-semibold">{pick(c.name, lang)}</div>
                                <div className="mt-1 text-xs text-white/80 line-clamp-2">{pick(c.description, lang)}</div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- OFFERS PREVIEW ---------- */
function OffersPreview({ offers }) {
    const { t, lang } = useLang();
    return (
        <section className="py-20 md:py-28">
            <div className="container-x">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div className="max-w-2xl">
                        <div className="eyebrow">{t.offers.eyebrow}</div>
                        <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-balance">{t.offers.title}</h2>
                        <p className="mt-4 text-base md:text-lg text-brand-charcoal/70">{t.offers.subtitle}</p>
                    </div>
                    <Link to="/angebote" className="btn-primary" data-testid="offers-all">
                        {t.offers.all} <ChevronRight className="h-4 w-4" />
                    </Link>
                </div>

                {!offers?.length ? (
                    <div className="mt-12 rounded-2xl border border-dashed border-black/15 bg-white p-10 text-center text-sm text-brand-charcoal/60">
                        {t.offers.empty}
                    </div>
                ) : (
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {offers.slice(0, 6).map((o) => <OfferCard key={o.id} offer={o} lang={lang} t={t} />)}
                    </div>
                )}
            </div>
        </section>
    );
}

export function OfferCard({ offer, lang, t }) {
    const saving =
        offer.old_price && offer.price
            ? Math.round(((offer.old_price - offer.price) / offer.old_price) * 100)
            : null;

    return (
        <div className="card-clean overflow-hidden flex flex-col transition-transform hover:-translate-y-1" data-testid={`offer-card-${offer.id}`}>
            <div className="relative aspect-[4/3] overflow-hidden bg-brand-cream-dark">
                {offer.image_url && (
                    <img src={resolveImage(offer.image_url)} alt={pick(offer.title, lang)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                )}
                {offer.badge && (
                    <div className="absolute top-3 left-3 badge-pill">{offer.badge}</div>
                )}
                {offer.featured && (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-brand-green text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
                        <Sparkles className="h-3 w-3 text-brand-yellow" /> {t.offers.featured.split(" ")[0]}
                    </div>
                )}
            </div>
            <div className="p-5 flex-1 flex flex-col">
                <div className="font-display font-semibold text-lg text-brand-charcoal">{pick(offer.title, lang)}</div>
                {offer.description && pick(offer.description, lang) && (
                    <p className="mt-1 text-sm text-brand-charcoal/65 line-clamp-2">{pick(offer.description, lang)}</p>
                )}
                <div className="mt-4 flex items-end gap-3">
                    <div className="text-3xl font-display font-bold text-brand-red leading-none">
                        {typeof offer.price === "number" ? `€ ${offer.price.toFixed(2)}` : ""}
                    </div>
                    {offer.old_price && (
                        <div className="text-sm text-brand-charcoal/40 line-through">€ {offer.old_price.toFixed(2)}</div>
                    )}
                    {offer.unit && <div className="ml-auto text-xs text-brand-charcoal/50">/ {offer.unit}</div>}
                </div>
                {saving ? (
                    <div className="mt-2 text-xs font-semibold text-brand-green">{t.offers.save} {saving}%</div>
                ) : null}
                {offer.valid_until && (
                    <div className="mt-3 text-[11px] text-brand-charcoal/50">
                        {t.offers.until} {new Date(offer.valid_until).toLocaleDateString()}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------- ABOUT ---------- */
function AboutSection() {
    const { t } = useLang();
    return (
        <section className="py-20 md:py-28 bg-brand-cream-dark/30">
            <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
                <div className="relative">
                    <img
                        src="https://images.pexels.com/photos/10360444/pexels-photo-10360444.jpeg"
                        alt="Frische Auswahl"
                        className="rounded-3xl shadow-2xl aspect-[4/5] object-cover w-full"
                    />
                    <div className="absolute -bottom-6 -right-4 hidden md:flex flex-col items-center justify-center h-32 w-32 rounded-2xl bg-brand-yellow text-brand-charcoal shadow-xl">
                        <div className="font-display text-4xl font-bold">100%</div>
                        <div className="text-[10px] uppercase tracking-widest">Frische</div>
                    </div>
                </div>
                <div>
                    <div className="eyebrow">{t.about.eyebrow}</div>
                    <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-balance">{t.about.title}</h2>
                    <p className="mt-5 text-base md:text-lg text-brand-charcoal/75">{t.about.body1}</p>
                    <p className="mt-3 text-base text-brand-charcoal/65">{t.about.body2}</p>
                    <div className="mt-7 grid sm:grid-cols-2 gap-3">
                        {t.about.points.map((p, i) => (
                            <div key={i} className="flex items-start gap-3 rounded-xl bg-white border border-black/5 p-3.5">
                                <div className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-green text-brand-yellow text-xs font-bold">{i + 1}</div>
                                <div className="text-sm text-brand-charcoal/80">{p}</div>
                            </div>
                        ))}
                    </div>
                    <Link to="/ueber-uns" className="mt-7 btn-outline-dark inline-flex" data-testid="about-more">
                        {t.cta.more} <ChevronRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

/* ---------- LOCATION ---------- */
export function LocationSection() {
    const { t } = useLang();
    const { settings } = useSettings();
    const hours = settings.opening_hours || {};
    const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    const directions = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(settings.address || "Sulzbacher Straße 10, 71522 Backnang")}`;

    return (
        <section id="standort" className="py-20 md:py-28">
            <div className="container-x">
                <div className="max-w-2xl">
                    <div className="eyebrow">{t.location.eyebrow}</div>
                    <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-balance">{t.location.title}</h2>
                </div>

                <div className="mt-12 grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7 rounded-3xl overflow-hidden border border-black/5 shadow-xl bg-white aspect-[16/10]">
                        <iframe
                            title="SCHOCK MARKT Standort"
                            src={settings.map_embed_url || "https://www.google.com/maps?q=Sulzbacher+Stra%C3%9Fe+10,+71522+Backnang&output=embed"}
                            className="h-full w-full"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                    <div className="lg:col-span-5 rounded-3xl bg-brand-green text-white p-7 md:p-9 flex flex-col">
                        <div className="font-display text-2xl font-semibold">SCHOCK MARKT</div>
                        <div className="mt-2 text-white/80 text-sm flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-brand-yellow" />{settings.address || "Sulzbacher Straße 10, 71522 Backnang"}</div>
                        {settings.phone && <div className="mt-1 text-white/80 text-sm flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 text-brand-yellow" />{settings.phone}</div>}
                        {settings.email && <div className="mt-1 text-white/80 text-sm flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-brand-yellow" />{settings.email}</div>}

                        <div className="mt-6 border-t border-white/10 pt-4">
                            <div className="text-[11px] uppercase tracking-widest text-brand-yellow flex items-center gap-2"><Clock className="h-3.5 w-3.5" />{t.location.hours}</div>
                            <ul className="mt-3 space-y-1.5 text-sm">
                                {dayKeys.map((k) => (
                                    <li key={k} className="flex justify-between gap-4">
                                        <span className="text-white/60">{t.location.daysShort[k]}</span>
                                        <span className="font-medium">{hours[k] || "—"}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="mt-6 text-xs text-white/70">{t.location.parking}</div>

                        <a href={directions} target="_blank" rel="noreferrer noopener" data-testid="location-directions" className="mt-5 btn-yellow self-start">
                            <Navigation className="h-4 w-4" /> {t.location.directions}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------- WHATSAPP CTA ---------- */
export function WhatsappCTA() {
    const { t } = useLang();
    const { settings } = useSettings();
    return (
        <section className="py-16 md:py-20">
            <div className="container-x">
                <div className="relative overflow-hidden rounded-3xl bg-brand-green text-white p-10 md:p-14">
                    <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-yellow/15 blur-3xl" />
                    <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-brand-red/10 blur-3xl" />
                    <div className="relative grid md:grid-cols-12 gap-6 items-center">
                        <div className="md:col-span-8">
                            <div className="eyebrow text-brand-yellow">{t.whatsappCta.eyebrow}</div>
                            <h3 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-balance">{t.whatsappCta.title}</h3>
                            <p className="mt-3 text-white/75 max-w-xl">{t.whatsappCta.body}</p>
                        </div>
                        <div className="md:col-span-4 md:justify-self-end">
                            <a
                                href={buildWhatsappLink(settings.whatsapp, "Hallo SCHOCK MARKT!")}
                                target="_blank" rel="noreferrer noopener"
                                data-testid="cta-whatsapp"
                                className="btn-yellow"
                            >
                                <MessageCircle className="h-4 w-4" /> {t.whatsappCta.cta}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------- NEWSLETTER ---------- */
export function NewsletterSection() {
    const { t, lang } = useLang();
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) {
            toast.error("Bitte Zustimmung erteilen.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/newsletter", { email, consent, language: lang });
            toast.success(t.newsletter.success);
            setEmail("");
            setConsent(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="py-16 md:py-20 bg-brand-cream-dark/40">
            <div className="container-x grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-6">
                    <div className="eyebrow">{t.newsletter.eyebrow}</div>
                    <h3 className="mt-3 font-display font-bold text-3xl md:text-4xl text-balance">{t.newsletter.title}</h3>
                    <p className="mt-3 text-brand-charcoal/70 max-w-md">{t.newsletter.body}</p>
                </div>
                <form className="lg:col-span-6 card-clean p-6 md:p-8" onSubmit={submit} data-testid="newsletter-form">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2" htmlFor="news-email">E-Mail</label>
                    <input
                        id="news-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t.newsletter.emailPlaceholder}
                        className="input-clean"
                        data-testid="newsletter-email"
                    />
                    <label className="mt-4 flex items-start gap-3 text-xs text-brand-charcoal/70">
                        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-black/20" data-testid="newsletter-consent" />
                        <span>{t.newsletter.consent}</span>
                    </label>
                    <button type="submit" disabled={submitting} className="mt-5 btn-primary disabled:opacity-50" data-testid="newsletter-submit">
                        {submitting ? "..." : t.cta.newsletter}
                    </button>
                </form>
            </div>
        </section>
    );
}

/* ---------- PAGE ---------- */
export default function Home() {
    const { settings } = useSettings();
    const [categories, setCategories] = useState([]);
    const [offers, setOffers] = useState([]);

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
        api.get("/offers", { params: { featured: true } }).then((r) => setOffers(r.data || [])).catch(() => {});
    }, []);

    const soon = isOpeningSoon(settings);

    return (
        <PageShell>
            {soon ? <HeroSoon settings={settings} /> : <HeroLive settings={settings} />}
            <ValueProps />
            <CategoriesPreview categories={categories} />
            <OffersPreview offers={offers.length ? offers : []} />
            <AboutSection />
            <LocationSection />
            <WhatsappCTA />
            <NewsletterSection />
        </PageShell>
    );
}
